import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { FaCheck } from "react-icons/fa6";
import { toast } from "../../utils/toast";
import Cookies from "js-cookie";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import ReactSelector from "../../components/common/CommonReactSelector";

export const MODAL_FOOTER_ROW_CLASS =
  "-mx-6 -mb-5 flex items-center justify-end gap-3 border-t border-[#E5E7EB] px-6 py-4";
export const MODAL_FOOTER_BUTTON_CLASS = "!h-10 !min-h-[40px] !max-h-10 rounded-lg !px-5";

const RadioOptionCard = ({ name, value, checked, onChange, label, testId, ariaLabel, isRtl = false }) => (
  <label
    className={`flex h-[46px] cursor-pointer items-center gap-2 rounded-lg border px-4 transition-all ${
      isRtl ? "flex-row-reverse text-right justify-end" : ""
    } ${
      checked
        ? "border-[#3F6FF5] bg-[#EFF6FF]"
        : "border-[#D9E1EC] bg-white hover:border-[#93C5FD]"
    }`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel || label}
      data-testid={testId}
      className="h-4 w-4 shrink-0 border-slate-300 text-[#3F6FF5] focus:ring-[#3F6FF5]"
    />
    <span className={`text-sm font-medium ${checked ? "text-[#2454D8]" : "text-[#475569]"} ${isRtl ? "text-right" : ""}`}>{label}</span>
  </label>
);

const ColumnMapStepper = ({ steps, currentStep, ariaLabel }) => (
  <ol
    data-testid="column-map-stepper"
    className="mb-5 flex w-full items-start"
    aria-label={ariaLabel}
  >
    {steps.map((step, index) => {
      const isCompleted = currentStep > step.id;
      const isActive = currentStep === step.id;
      const isLast = index === steps.length - 1;

      return (
        <li
          key={step.id}
          data-testid={step.testId}
          className={`flex min-w-0 items-start ${isLast ? "flex-none" : "flex-1"}`}
        >
          <div className="flex w-[72px] shrink-0 flex-col items-center">
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all ${
                isCompleted
                  ? "bg-[#45B369] text-white"
                  : isActive
                    ? "bg-[#3F63EA] text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
              aria-current={isActive ? "step" : undefined}
            >
              {isCompleted ? <FaCheck className="text-xs" /> : step.id}
            </div>
            <p
              className={`mt-2 whitespace-nowrap text-center text-xs font-medium leading-tight ${
                isActive
                  ? "text-slate-700"
                  : isCompleted
                    ? "text-slate-700"
                    : "text-slate-400"
              }`}
            >
              {step.label}
            </p>
          </div>
          {!isLast && (
            <div
              className={`mx-1 mt-3.5 h-px min-w-[12px] flex-1 ${
                isCompleted ? "bg-[#45B369]" : "bg-[#E5E7EB]"
              }`}
              aria-hidden="true"
            />
          )}
        </li>
      );
    })}
  </ol>
);

const MappingFieldSelect = ({
  label,
  value,
  options,
  onChange,
  theme,
  placeholder,
  isRtl = false,
}) => {
  const selectOptions = options.map((option) => ({ value: option, label: option }));
  const selectedOption = selectOptions.find((option) => option.value === value) || null;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={`text-sm font-medium text-[#374151] ${isRtl ? "text-right" : ""} ${
          theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
        }`}
        data-testid={`column-map-step-mandatory-fields-label-${label}`}
      >
        {label}
      </label>
      <ReactSelector
        value={selectedOption}
        options={selectOptions}
        onChange={(option) => onChange({ target: { value: option?.value ?? "" } })}
        placeholder={placeholder}
        isClearable
        height="36px"
        borderRadius="8px"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
        maxMenuHeight="200px"
        dataTestId={`column-map-mapping-field-select-${label}`}
      />
    </div>
  );
};

export const ColumnMap = ({
  fetchData,
  setUploadProgress,
  mapopen,
  setMapOpen,
  colHeaders,
  optionalFields,
  mandatoryFields,
  setModalValue,
  open,
  setOpen,
  newFile,
  selectedOption,
  cycle,
  setShowProgress,
  setFile,
  resetFileStates,
  abortController,
  setAbortController,
  onUploadSuccess,
  onBackToUpload,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isNewStructure, setIsNewStructure] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allowUpdateIfExists, setAllowUpdateIfExists] = useState(false);
  const xhrRef = useRef(null);
  const [taskPriority, setTaskPriority] = useState("Low_Priority");
  const [matchedColumns, setmatchedColumns] = useState({});
  const { theme, langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";

  useEffect(() => {
    if (open && xhrRef.current) {
      xhrRef.current.abort();
      toast.error(translations[langMode].fileuploadedaborteddue);
      setUploadProgress(0);
    }
  }, [open]);

  // This modal stays mounted for as long as a file is selected, and a successful
  // upload doesn't clear that file — so the wizard state outlives the import. Without
  // this, importing a second sheet (or switching workflow first) reopens on the step
  // the last run ended on, still holding the previous sheet's column mappings.
  const importSessionRef = useRef({ file: null, workflow: null });
  useEffect(() => {
    if (!mapopen) return;
    const { file: lastFile, workflow: lastWorkflow } = importSessionRef.current;
    // Stepping back to the Upload File screen and returning keeps the same file and
    // workflow, so mappings made so far are intentionally preserved there.
    if (lastFile === newFile && lastWorkflow === selectedOption) return;

    importSessionRef.current = { file: newFile, workflow: selectedOption };
    setCurrentStep(1);
    setmatchedColumns({});
    setTaskPriority("Low_Priority");
    setAllowUpdateIfExists(false);
    setShowConfirmation(false);
    setIsNewStructure(false);
  }, [mapopen, newFile, selectedOption]);

  const uploadFile = async (
    file,
    selectedOption,
    matchedColumns,
    taskPriority
  ) => {
    const formData = new FormData();
    formData.append("excel", file);
    formData.append("workflowId", selectedOption);
    formData.append("columnMappings", JSON.stringify(matchedColumns));
    formData.append("taskPriority", taskPriority);
    formData.append("allowUpdateIfExists", allowUpdateIfExists || false);
    // Cycle ("YYYY-MM") the sheet's tasks belong to, picked on the import
    // toolbar. Omitted rather than sent empty when nothing is selected.
    if (cycle) {
      formData.append("cycle", cycle);
    }
    const url = `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/task/excel-upload`;
    try {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open("POST", url);
      const accessToken = Cookies.get("accessToken");
      if (accessToken) {
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      }
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          setUploadProgress(percentComplete);
        }
      };
      xhr.onload = () => {
        if (xhr.status === 200) {
          const responseData = JSON.parse(xhr.responseText);
          if (responseData.header.code === 600) {
            setUploadProgress(100);
            toast.success(responseData.body.value);
            setShowProgress(false);
            fetchData();
            onUploadSuccess?.();
          } else {
            setOpen(true);
            setModalValue(responseData.body.value);
            setShowProgress(false);
          }
        } else {
          console.error("File upload failed", xhr.status);
          setShowProgress(false);
        }
      };
      xhr.onerror = () => {};
      xhr.send(formData);
    } catch (error) {}
  };

  // Mapping every mandatory field is required before leaving the Mandatory Fields
  // step or submitting, so the guard is shared by the buttons and the handlers.
  const missingMandatoryFields = useMemo(
    () => (mandatoryFields || []).filter((field) => !matchedColumns[field]),
    [mandatoryFields, matchedColumns]
  );
  const allMandatoryFieldsSelected = missingMandatoryFields.length === 0;

  const handleNext = () => {
    if (currentStep === 1) {
      if (isNewStructure === null) {
        toast.error(translations[langMode].plxselectanoption);
      } else if (isNewStructure) {
        setShowConfirmation(true);
      } else {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (!allMandatoryFieldsSelected) {
        toast.error(translations[langMode].pleasefillallmandtoryfield);
        return;
      }
      setCurrentStep(3);
    } else {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      setCurrentStep(2);
    }
  };

  const handleSubmit = () => {
    if (!allMandatoryFieldsSelected) {
      toast.error(translations[langMode].pleasefillallmandtoryfield);
      return;
    }

    const controller = new AbortController();
    setAbortController(controller);

    // The progress card belongs to the actual upload, so it only shows from here on.
    setUploadProgress(0);
    setShowProgress(true);

    setTimeout(() => {
      if (controller.signal.aborted) {
        toast.info(translations[langMode].uploadcanceled);
        resetFileStates(); 
        return;
      }
      uploadFile(newFile, selectedOption, matchedColumns, taskPriority);
    }, 3000); 
    setMapOpen(false);
  };
  const handleSelectChange = (fieldLabel, value) => {
    setmatchedColumns((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
  };

  const getFilteredOptions = (fieldLabel) => {
    const currentValue = matchedColumns[fieldLabel];
    return colHeaders.filter(
      (option) =>
        !Object.values(matchedColumns).includes(option) ||
        option === currentValue
    );
  };
  const handleClose = () => {
    setCurrentStep(1);
    setMapOpen(false);
    setShowProgress(false);
    setUploadProgress(0);
    setFile(null);
  };

  // Step back to the Upload File screen without discarding the already-selected
  // file (unlike handleClose, which fully cancels the import).
  const handleBackToUpload = () => {
    setCurrentStep(1);
    if (onBackToUpload) {
      onBackToUpload();
      return;
    }
    setMapOpen(false);
  };

  const showOptionalStep = optionalFields.length > 0;
  const stepperSteps = useMemo(
    () => [
      {
        id: 1,
        label: translations[langMode].uploadfilelabel,
        testId: "column-map-step-upload-file",
      },
      {
        id: 2,
        label: (translations[langMode].taskpriority || translations[langMode].priority).trim(),
        testId: "column-map-step-task-priority",
      },
      {
        id: 3,
        label: `${translations[langMode].Mandatory} ${translations[langMode].Fields}`,
        testId: "column-map-step-mandatory-fields",
      },
      ...(showOptionalStep
        ? [
            {
              id: 4,
              label: `${translations[langMode].Other} ${translations[langMode].Fields}`,
              testId: "column-map-step-other-fields",
            },
          ]
        : []),
    ],
    [langMode, showOptionalStep]
  );

  const inputThemeClass =
    theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "";

  // Plain overlay (no Headless UI Dialog): its portal/focus-trap made stepping in
  // from the upload modal flash, and it fought the portaled react-select menus.
  useEffect(() => {
    if (!mapopen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mapopen]);

  if (!mapopen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="column-map-modal-title"
      dir={isRtl ? "rtl" : "ltr"}
    >
            <div
              data-testid="import-csv-column-map-modal"
              className={`relative flex max-h-[90vh] w-full max-w-[672px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ${isRtl ? "text-right" : "text-left"} ${inputThemeClass}`}
            >
              <div className={`border-b border-[#E5E7EB] bg-white px-6 py-5 pr-14 ${isRtl ? "text-right" : ""}`}>
                <h2
                  id="column-map-modal-title"
                  className="text-base font-semibold text-[#1E293B]"
                  data-testid="import-csv-column-map-modal-title"
                >
                  {translations[langMode].importtaskusingsheet}
                </h2>
                <p className="mt-1 text-sm text-[#6B7280]" data-testid="import-csv-column-map-modal-description">
                  {translations[langMode].mapyourfilecolumns}
                </p>
                <ModalCloseButton
                  onClick={handleClose}
                  className={`absolute top-4 border-0 bg-transparent shadow-none hover:border-0 ${isRtl ? "left-4" : "right-4"}`}
                  ariaLabel={translations[langMode].cancelandclose}
                />
              </div>

              <div className={`flex-1 overflow-y-auto px-6 py-5 ${isRtl ? "text-right" : ""}`}>
                <ColumnMapStepper
                  steps={stepperSteps}
                  currentStep={currentStep + 1}
                  ariaLabel={translations[langMode].columnmappingsteps}
                />

                {currentStep === 1 && (
                  <div className="flex flex-col gap-5">
                    <section className={inputThemeClass}>
                      <h3 className="text-sm font-semibold text-[#111827]" data-testid="column-map-step-task-priority-title">
                        {translations[langMode].taskpriority?.trim() || translations[langMode].priority}
                      </h3>
                      <p className="mt-1 text-xs text-[#6B7280]" data-testid="column-map-step-task-priority-description">
                        {translations[langMode].selecttaskpriority}
                      </p>
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <RadioOptionCard
                          name="taskPriority"
                          value="Low_Priority"
                          checked={taskPriority === "Low_Priority"}
                          onChange={() => setTaskPriority("Low_Priority")}
                          label={translations[langMode].lowPriority}
                          testId="column-map-step-task-priority-low-priority"
                          isRtl={isRtl}
                        />
                        <RadioOptionCard
                          name="taskPriority"
                          value="Medium_Priority"
                          checked={taskPriority === "Medium_Priority"}
                          onChange={() => setTaskPriority("Medium_Priority")}
                          label={translations[langMode].mediumPriority}
                          testId="column-map-step-task-priority-medium-priority"
                          isRtl={isRtl}
                        />
                        <RadioOptionCard
                          name="taskPriority"
                          value="High_Priority"
                          checked={taskPriority === "High_Priority"}
                          onChange={() => setTaskPriority("High_Priority")}
                          label={translations[langMode].highPriority}
                          testId="column-map-step-task-priority-high-priority"
                          isRtl={isRtl}
                        />
                      </div>
                    </section>
                    <div className={MODAL_FOOTER_ROW_CLASS}>
                      <CommonButton colorIntent="primary"
                        onClick={handleBackToUpload}
                        variant="outline"
                        theme={theme}
                        className={MODAL_FOOTER_BUTTON_CLASS}
                        tooltipText={translations[langMode].gobacktopreviousstep}
                        tooltipPosition="top"
                        data-testid="column-map-step1-back-button"
                      >
                        {translations[langMode].Back}
                      </CommonButton>
                      <CommonButton colorIntent="primary"
                        onClick={handleNext}
                        variant="primary"
                        theme={theme}
                        className={MODAL_FOOTER_BUTTON_CLASS}
                        tooltipText={translations[langMode].continuetonextstep}
                        tooltipPosition="top"
                        data-testid="column-map-next-button"
                      >
                        {translations[langMode].next}
                      </CommonButton>
                    </div>
                  </div>
                )}
                {currentStep === 2 && (
                  <div className="flex flex-col gap-5">
                    <section
                      className={`rounded-lg border border-[#FECACA] bg-[#FFF1F2] p-4 ${inputThemeClass}`}
                      data-testid="column-map-step-what-do-you-want-existing-accounts-text"
                    >
                      <h3 className="text-sm font-semibold text-[#B91C1C]">
                        {translations[langMode].existingaccountsquestion}
                      </h3>
                      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <RadioOptionCard
                          name="Should_be_Updated_Exit_Account"
                          value="true"
                          checked={allowUpdateIfExists === true}
                          onChange={() => setAllowUpdateIfExists(true)}
                          label={translations[langMode].updateexistingaccountentries}
                          testId="column-map-step-update-already-existing-account-entries"
                          isRtl={isRtl}
                        />
                        <RadioOptionCard
                          name="Create_new_Account"
                          value="false"
                          checked={allowUpdateIfExists === false}
                          onChange={() => setAllowUpdateIfExists(false)}
                          label={translations[langMode].createnewaccountentries}
                          testId="column-map-step-create-new-account"
                          isRtl={isRtl}
                        />
                      </div>
                    </section>

                    <section className={inputThemeClass}>
                      <h3 className="text-sm font-semibold text-[#B91C1C]" data-testid="column-map-step-mandatory-fields-title">
                        {translations[langMode].mandatoryfield} <span className="text-[#EF4444]">*</span>
                      </h3>
                      <p className="mt-1 text-xs text-[#6B7280]" data-testid="column-map-step-mandatory-fields-description">
                        {translations[langMode].pleasefillallmandtoryfield}
                      </p>
                      <div className="mt-3 grid grid-cols-1 gap-3">
                        {mandatoryFields.map((label, index) => (
                          <MappingFieldSelect
                            key={index}
                            label={`${label}${index === 0 ? ` - ${translations[langMode].primarykeyvalue}` : ""}`}
                            value={matchedColumns[label] || ""}
                            options={getFilteredOptions(label)}
                            onChange={(e) => handleSelectChange(label, e.target.value)}
                            theme={theme}
                            placeholder={translations[langMode].selectavalue}
                            isRtl={isRtl}
                          />
                        ))}
                      </div>
                    </section>

                    <div className={MODAL_FOOTER_ROW_CLASS}>
                      <CommonButton colorIntent="primary"
                        onClick={handleBack}
                        variant="outline"
                        theme={theme}
                        className={MODAL_FOOTER_BUTTON_CLASS}
                        tooltipText={translations[langMode].gobacktopreviousstep}
                        tooltipPosition="top"
                        data-testid="column-map-step2-back-button"
                      >
                        {translations[langMode].Back}
                      </CommonButton>
                      {isNewStructure || optionalFields.length === 0 ? (
                        <CommonButton colorIntent="success"
                          onClick={handleSubmit}
                          variant="success"
                          disabled={!allMandatoryFieldsSelected}
                          className={MODAL_FOOTER_BUTTON_CLASS}
                          tooltipText={
                            allMandatoryFieldsSelected
                              ? translations[langMode].submitmappeddataandstartupload
                              : translations[langMode].pleasefillallmandtoryfield
                          }
                          tooltipPosition="top"
                          data-testid="column-map-step2-submit-button"
                        >
                          {translations[langMode].submit}
                        </CommonButton>
                      ) : (
                        <CommonButton colorIntent="primary"
                          onClick={handleNext}
                          variant="primary"
                          theme={theme}
                          disabled={!allMandatoryFieldsSelected}
                          className={MODAL_FOOTER_BUTTON_CLASS}
                          tooltipText={
                            allMandatoryFieldsSelected
                              ? translations[langMode].continuetonextstep
                              : translations[langMode].pleasefillallmandtoryfield
                          }
                          tooltipPosition="top"
                          data-testid="column-map-step2-next-button"
                        >
                          {translations[langMode].next}
                        </CommonButton>
                      )}
                    </div>
                  </div>
                )}
                {currentStep === 3 && optionalFields.length > 0 && (
                  <div className="flex flex-col gap-5" data-testid="column-map-step3-panel">
                    <section className={`rounded-xl border border-[#E5E7EB] bg-white p-5 ${inputThemeClass}`}>
                      <h3 className="text-sm font-semibold text-[#111827]">
                        {translations[langMode].Other} {translations[langMode].Fields}
                      </h3>
                      <p className="mt-1 text-xs text-[#6B7280]" data-testid="column-map-step-other-fields-description">
                        {translations[langMode].NonMandatoryFields}
                      </p>
                      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {optionalFields.map((label, index) => (
                          <MappingFieldSelect
                            key={index}
                            label={label}
                            value={matchedColumns[label] || ""}
                            options={getFilteredOptions(label)}
                            onChange={(e) => handleSelectChange(label, e.target.value)}
                            theme={theme}
                            placeholder={translations[langMode].selectavalue}
                            isRtl={isRtl}
                          />
                        ))}
                      </div>
                    </section>
                    <div className={MODAL_FOOTER_ROW_CLASS}>
                      <CommonButton colorIntent="primary"
                        onClick={handleBack}
                        variant="outline"
                        theme={theme}
                        className={MODAL_FOOTER_BUTTON_CLASS}
                        tooltipText={translations[langMode].gobacktomapmandotoryfield}
                        tooltipPosition="top"
                        data-testid="column-map-step3-back-button"
                      >
                        {translations[langMode].Back}
                      </CommonButton>
                      <CommonButton colorIntent="success"
                        onClick={handleSubmit}
                        variant="success"
                        disabled={!allMandatoryFieldsSelected}
                        className={MODAL_FOOTER_BUTTON_CLASS}
                        tooltipText={
                          allMandatoryFieldsSelected
                            ? translations[langMode].submitmappeddataandstartupload
                            : translations[langMode].pleasefillallmandtoryfield
                        }
                        tooltipPosition="left"
                        data-testid="column-map-step3-submit-button"
                      >
                        {translations[langMode].submit}
                      </CommonButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
    </div>
  );
};
